import type { ArtifactType, ConvertAndCopyFormat } from "~/services/artifacts";

export interface FormatOption {
  value: ConvertAndCopyFormat;
  label: string;
  description: string;
}

// Format options per artifact type for download/export dialogs. Kept narrow
// on purpose — only the formats the convert-and-copy Lambda actually
// produces for that artifact kind. "original" is always last as the
// "as-stored" passthrough.
const OPTIONS: Record<ArtifactType, readonly FormatOption[]> = {
  dataframe: [
    { value: "csv", label: "CSV", description: "Comma-separated values, opens in Excel/Sheets" },
    {
      value: "parquet",
      label: "Parquet",
      description: "Columnar format, efficient for large datasets",
    },
    { value: "zip", label: "ZIP", description: "Compressed archive of partition files" },
  ],
  figure: [
    { value: "html", label: "HTML", description: "Standalone HTML file with interactive chart" },
  ],
  value: [{ value: "original", label: "Pickle", description: "Python pickle file" }],
  text: [{ value: "original", label: "Original", description: "As stored" }],
  blob: [{ value: "original", label: "Original", description: "As stored" }],
  image: [{ value: "original", label: "Original", description: "As stored" }],
  excel: [{ value: "original", label: "Original", description: "Excel workbook (.xlsx)" }],
  table: [{ value: "original", label: "JSON", description: "The sampled rows (.json)" }],
};

export const formatOptionsFor = (type: ArtifactType): readonly FormatOption[] => OPTIONS[type];

// Default format we send when the user clicks the primary download button
// without picking from the menu. Matches the artifact's native form so
// nothing is converted unnecessarily.
export const defaultFormatFor = (type: ArtifactType): ConvertAndCopyFormat => {
  switch (type) {
    case "dataframe":
      return "csv";
    case "figure":
      return "html";
    case "value":
      return "original";
    default:
      return "original";
  }
};

// Display labels — uppercase extension reads cleaner in menus than the
// raw enum value (which we still send on the wire).
export const formatLabel: Record<ConvertAndCopyFormat, string> = {
  csv: "CSV",
  parquet: "Parquet",
  zip: "ZIP",
  html: "HTML",
  pickle: "Pickle",
  original: "Original",
};
